import React, { useState, useEffect, forwardRef } from 'react';
import { Box, Card, CardContent, CardHeader, Stack, TextField, Typography } from '@mui/material';
import MaskedInput from 'react-text-mask';
import zxcvbn from 'zxcvbn';
import { useUser } from '../../contexts/UserContext';
import { ParentFormProps, TextMaskCustomProps } from '../../types';
import { isValidPassword } from '../../utils';
import useSettings from '../../hooks/useSettings';
import { EVENTS_THEME_BACKGROUND_COLOR } from '../../constants';

const TextMaskCustom = forwardRef<MaskedInput, TextMaskCustomProps>(function TextMaskCustom(props, ref) {
    const { onChange, placeholder, ...other } = props;

    return (
        <MaskedInput
            {...other}
            ref={ref}
            mask={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
            placeholderChar={'\u2000'}
            showMask={!!placeholder}
            onChange={onChange}
        />
    );
});

const ParentForm: React.FC<ParentFormProps> = ({
    title,
    subtitle,
    disableInputs,
    onParentDataChange,
    onPasswordChange,
}) => {
    const { user } = useUser();
    const { backgroundColor } = useSettings();
    const [isPhoneFocused] = useState(false);

    const [parentData, setParentData] = useState({
        fname: user?.fname || '',
        lname: user?.lname || '',
        email: user?.email || '',
        phone: user?.phone || '',
        password: '',
        confirmPassword: '',
    });

    const [passwordStrength, setPasswordStrength] = useState(0);

    useEffect(() => {
        if (user) {
            setParentData({
                fname: user.fname,
                lname: user.lname,
                email: user.email,
                phone: user.phone,
                password: '',
                confirmPassword: '',
            });
        }
    }, [user]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const updatedData = { ...parentData, [name]: value };
        setParentData(updatedData);

        if (onParentDataChange) {
            onParentDataChange(updatedData);
        }

        if (name === 'confirmPassword' || name === 'password') {
            if (
                parentData.password === parentData.confirmPassword &&
                parentData.password !== '' &&
                parentData.confirmPassword !== ''
            ) {
                onPasswordChange?.(parentData.password);
            } else {
                onPasswordChange?.('');
            }
        }
    };

    useEffect(() => {
        const strength = zxcvbn(parentData.password).score;
        setPasswordStrength(strength);
    }, [parentData.password]);

    return (
        <Card 
            elevation={4}
            className="single-form"
            sx={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderLeft: `4px solid ${backgroundColor || EVENTS_THEME_BACKGROUND_COLOR}`,
                backgroundColor: 'background.paper',
            }}
        >
            <CardHeader 
                title={title}
                subheader={subtitle || undefined}
                titleTypographyProps={{ variant: 'h5', fontWeight: 600 }}
                sx={{
                    backgroundColor: 'rgba(41, 171, 226, 0.05)',
                    borderBottom: '1px solid rgba(41, 171, 226, 0.1)',
                }}
            />
            <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                    <TextField
                        name="fname"
                        required
                        fullWidth
                        label="Parent First Name"
                        variant="outlined"
                        value={parentData.fname}
                        disabled={disableInputs}
                        onChange={handleInputChange}
                        inputProps={{ maxLength: 50 }}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: 'text.primary',
                            },
                            '& .MuiInputBase-input:disabled': {
                                color: 'text.primary',
                                WebkitTextFillColor: 'text.primary',
                                opacity: 0.7,
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                            },
                        }}
                    />
                    <TextField
                        name="lname"
                        required
                        fullWidth
                        label="Parent Last Name"
                        variant="outlined"
                        value={parentData.lname}
                        disabled={disableInputs}
                        onChange={handleInputChange}
                        inputProps={{ maxLength: 50 }}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: 'text.primary',
                            },
                            '& .MuiInputBase-input:disabled': {
                                color: 'text.primary',
                                WebkitTextFillColor: 'text.primary',
                                opacity: 0.7,
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                            },
                        }}
                    />
                    <TextField
                        name="email"
                        required
                        fullWidth
                        label="Parent Email"
                        type="email"
                        variant="outlined"
                        value={parentData.email}
                        disabled={disableInputs}
                        onChange={handleInputChange}
                        inputProps={{ maxLength: 100 }}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: 'text.primary',
                            },
                            '& .MuiInputBase-input:disabled': {
                                color: 'text.primary',
                                WebkitTextFillColor: 'text.primary',
                                opacity: 0.7,
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                            },
                        }}
                    />
                    <TextField
                        name="phone"
                        required
                        fullWidth
                        label="Parent Phone Number"
                        type="tel"
                        variant="outlined"
                        value={parentData.phone}
                        disabled={disableInputs}
                        onChange={handleInputChange}
                        InputProps={{
                            inputComponent: TextMaskCustom as any,
                            inputProps: {
                                placeholder: isPhoneFocused ? '___-___-____' : null,
                            },
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: 'text.primary',
                            },
                            '& .MuiInputBase-input:disabled': {
                                color: 'text.primary',
                                WebkitTextFillColor: 'text.primary',
                                opacity: 0.7,
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                            },
                        }}
                    />
                    {!disableInputs && (
                        <>
                            <Box>
                                <TextField
                                    name="password"
                                    required
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    variant="outlined"
                                    value={parentData.password}
                                    disabled={disableInputs}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 20 }}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: 'text.primary',
                                        },
                                        '& .MuiInputBase-input:disabled': {
                                            color: 'text.primary',
                                            WebkitTextFillColor: 'text.primary',
                                            opacity: 0.7,
                                        },
                                    }}
                                />
                                {parentData.password.length > 0 && (
                                    <Typography 
                                        variant="caption" 
                                        color={passwordStrength > 2 ? 'primary' : 'error'}
                                        sx={{ mt: 1, display: 'block' }}
                                    >
                                        Password Strength: {['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][passwordStrength]}.
                                    </Typography>
                                )}
                                {!isValidPassword(parentData.password) && parentData.password.length > 0 && (
                                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                        A minimum 8 characters password contains a combination of <strong>uppercase, lowercase letter</strong>, <strong>numbers</strong> and special characters like <strong>@$!%*#?&</strong>
                                    </Typography>
                                )}
                            </Box>
                            <TextField
                                name="confirmPassword"
                                required
                                fullWidth
                                label="Confirm Password"
                                type="password"
                                variant="outlined"
                                value={parentData.confirmPassword}
                                disabled={disableInputs}
                                onChange={handleInputChange}
                                inputProps={{ maxLength: 20 }}
                                error={
                                    parentData.password !== parentData.confirmPassword &&
                                    parentData.confirmPassword !== ''
                                }
                                helperText={
                                    parentData.password !== parentData.confirmPassword &&
                                        parentData.confirmPassword !== ''
                                        ? 'Passwords do not match'
                                        : ''
                                }
                                sx={{
                                    '& .MuiInputBase-input': {
                                        color: 'text.primary',
                                    },
                                    '& .MuiInputBase-input:disabled': {
                                        color: 'text.primary',
                                        WebkitTextFillColor: 'text.primary',
                                        opacity: 0.7,
                                    },
                                }}
                            />
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default ParentForm;
